import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Cell, ComposedChart, Area,
} from 'recharts';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSelectedCity } from '../contexts/SelectedCityContext';
import useDashboardData from '../hooks/useDashboardData';
import dashboardService from '../services/dashboardService';
import { getSimuladosAnalytics } from '../services/dashboardService';
import { ErrorDisplay } from '../components/dashboard';
import PainelRede from './PainelRede';
import { abaOculta } from '../config/navigationConfig';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import SearchableSelect from '../components/SearchableSelect';
import { ehAlunoMatriculado } from '../utils/statusAluno';

// ── Paleta SIDEB ──
const COLORS = {
  green: '#13983D',
  navy: '#2E4974',
  blue: '#0974BA',
  yellow: '#F9B047',
  red: '#E83A3C',
  teal: '#13983D',   // alias para green (retrocompat)
  purple: '#2E4974', // usa navy como substituto
  orange: '#F9B047', // usa yellow como substituto
  pink: '#E83A3C',   // usa red como substituto
};
const PALETTE = [COLORS.blue, COLORS.teal, COLORS.yellow, COLORS.red, COLORS.purple, COLORS.green, COLORS.orange, COLORS.pink];
const MEDIA_ESTADUAL = 5.7;

// ── Componente de Card de Estatística Compacto ──
function StatMiniCard({ title, value, subtitle, icon, color, darkMode }) {
  const colorMap = {
    blue: { bg: 'from-[#0974BA]/10 to-[#0974BA]/5', border: 'border-[#0974BA]/20', text: 'text-[#0974BA]' },
    green: { bg: 'from-[#13983D]/10 to-[#13983D]/5', border: 'border-[#13983D]/20', text: 'text-[#13983D]' },
    purple: { bg: 'from-[#2E4974]/10 to-[#2E4974]/5', border: 'border-[#2E4974]/20', text: 'text-[#2E4974]' },
    orange: { bg: 'from-[#F9B047]/10 to-[#F9B047]/5', border: 'border-[#F9B047]/20', text: 'text-[#F9B047]' },
    red: { bg: 'from-[#E83A3C]/10 to-[#E83A3C]/5', border: 'border-[#E83A3C]/20', text: 'text-[#E83A3C]' },
    teal: { bg: 'from-[#0974BA]/10 to-[#0974BA]/5', border: 'border-[#0974BA]/20', text: 'text-[#0974BA]' },
    pink: { bg: 'from-[#E83A3C]/10 to-[#E83A3C]/5', border: 'border-[#E83A3C]/20', text: 'text-[#E83A3C]' },
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <div className={`rounded-2xl p-5 border backdrop-blur-xl transition-all duration-300 hover:scale-[1.02] ${
      darkMode
        ? `bg-gradient-to-br ${c.bg} ${c.border} border-opacity-30`
        : `bg-gradient-to-br ${c.bg} ${c.border} shadow-sm`
    }`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{icon}</span>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
          darkMode ? 'bg-slate-700/50 text-slate-400' : 'bg-slate-100 text-slate-500'
        }`}>{subtitle}</span>
      </div>
      <p className={`text-3xl font-bold ${c.text}`}>{value}</p>
      <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{title}</p>
    </div>
  );
}

// ── Componente de Card Container para Gráficos ──
function ChartCard({ title, subtitle, children, darkMode, className = '', fullWidth = false }) {
  return (
    <div className={`rounded-2xl p-6 backdrop-blur-xl border transition-all duration-300 ${
      darkMode
        ? 'bg-slate-800/70 border-slate-700/10'
        : 'bg-white/90 border-slate-200 shadow-sm'
    } ${fullWidth ? 'col-span-full' : ''} ${className}`}>
      <h3 className={`text-base font-semibold ${subtitle ? 'mb-1' : 'mb-5'} ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
        {title}
      </h3>
      {subtitle && (
        <p className={`text-xs mb-5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          {subtitle}
        </p>
      )}
      {children}
    </div>
  );
}

// ── Empty State ──
function EmptyChart({ message, darkMode }) {
  return (
    <div className={`flex flex-col items-center justify-center h-[300px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
      <svg className="w-16 h-16 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
      <p className="text-sm text-center">{message}</p>
      <p className="text-xs mt-1 opacity-70">Aplique simulados e importe as respostas para visualizar</p>
    </div>
  );
}

// ── Custom Tooltip ──
function CustomTooltip({ active, payload, label, darkMode, suffix = '' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className={`rounded-xl px-4 py-3 shadow-xl border text-sm ${
      darkMode ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
    }`}>
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="text-xs">
          {p.name}: <span className="font-bold">{typeof p.value === 'number' ? p.value.toFixed(1) : p.value}{suffix}</span>
        </p>
      ))}
    </div>
  );
}

// ── Candlestick-style bar for Descriptors ──
function DescriptorCandleChart({ data, darkMode }) {
  if (!data?.length) return <EmptyChart message="Sem dados de descritores" darkMode={darkMode} />;

  const chartData = data.slice(0, 20).map(d => ({
    code: d.code,
    name: d.name,
    rate: d.rate,
    min: d.min_rate,
    max: d.max_rate,
    q1: d.q1,
    q3: d.q3,
    median: d.median_rate,
    range: [d.q1, d.q3],
    whisker: [d.min_rate, d.max_rate],
  }));

  return (
    <ResponsiveContainer width="100%" height={400}>
      <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#334155' : '#e2e8f0'} />
        <XAxis
          dataKey="code"
          tick={{ fill: darkMode ? '#94a3b8' : '#64748b', fontSize: 10 }}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis
          domain={[0, 100]}
          tick={{ fill: darkMode ? '#94a3b8' : '#64748b', fontSize: 11 }}
          label={{ value: 'Taxa de Acertos (%)', angle: -90, position: 'insideLeft', style: { fill: darkMode ? '#94a3b8' : '#64748b', fontSize: 11 } }}
        />
        <Tooltip
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const d = payload[0]?.payload;
            if (!d) return null;
            return (
              <div className={`rounded-xl px-4 py-3 shadow-xl border text-sm max-w-xs ${
                darkMode ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
              }`}>
                <p className="font-bold text-xs mb-1">{d.code}</p>
                <p className="text-xs opacity-80 mb-2">{d.name}</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <span>Media:</span><span className="font-bold">{d.rate}%</span>
                  <span>Mediana:</span><span className="font-bold">{d.median}%</span>
                  <span>Min:</span><span>{d.min}%</span>
                  <span>Max:</span><span>{d.max}%</span>
                  <span>Q1:</span><span>{d.q1}%</span>
                  <span>Q3:</span><span>{d.q3}%</span>
                </div>
              </div>
            );
          }}
        />
        <ReferenceLine y={60} stroke={COLORS.yellow} strokeDasharray="5 5" label={{ value: 'Meta 60%', fill: COLORS.yellow, fontSize: 10 }} />
        <Bar dataKey="rate" fill={COLORS.blue} radius={[4, 4, 0, 0]} barSize={20}>
          {chartData.map((entry, index) => (
            <Cell key={index} fill={entry.rate >= 60 ? COLORS.teal : entry.rate >= 40 ? COLORS.yellow : COLORS.red} />
          ))}
        </Bar>
        <Line type="monotone" dataKey="max" stroke={COLORS.green} strokeWidth={1} dot={{ r: 3 }} name="Max" />
        <Line type="monotone" dataKey="min" stroke={COLORS.red} strokeWidth={1} dot={{ r: 3 }} name="Min" />
        <Legend wrapperStyle={{ fontSize: 11, color: darkMode ? '#94a3b8' : '#64748b' }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}


function DashboardSimulados() {
  const { getUserCity, hasRole } = useAuth();
  const { darkMode } = useTheme();
  const userCity = getUserCity();
  const isAdmin = hasRole(ROLES.ADMIN, ROLES.GESTOR, ROLES.COORDENADOR);

  // ── Filtro de cidade vem do sidebar (SelectedCityContext) ──
  const { cities, selectedCityId, loading: loadingCities } = useSelectedCity();

  // ── Filtros de simulado (todos os usuarios) ──
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState(null);

  // ── Dados de simulado (fetch independente com filtros) ──
  const [simData, setSimData] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  const selectedCityName = selectedCityId
    ? cities.find(c => c.id === selectedCityId)?.city
    : null;

  // Fetch simulados analytics com filtros
  const fetchSimulados = useCallback(async () => {
    setSimLoading(true);
    try {
      const data = await getSimuladosAnalytics({
        cityId: selectedCityId,
        schoolId: selectedSchoolId,
        classId: selectedClassId,
        grade: selectedGrade,
      });
      setSimData(data);
    } catch (err) {
      console.error('Erro ao buscar analytics de simulados:', err);
      setSimData(null);
    } finally {
      setSimLoading(false);
    }
  }, [selectedCityId, selectedSchoolId, selectedClassId, selectedGrade]);

  useEffect(() => {
    fetchSimulados();
  }, [fetchSimulados]);

  // Reset filtros dependentes ao mudar cidade
  useEffect(() => {
    setSelectedSchoolId(null);
    setSelectedGrade(null);
    setSelectedClassId(null);
  }, [selectedCityId]);

  // Reset turma ao mudar escola ou ano
  useEffect(() => {
    setSelectedClassId(null);
  }, [selectedSchoolId, selectedGrade]);

  const {
    loading, error, stats,
    idebEvolution, topSchoolsByIdeb, refresh,
    students, classes,
  } = useDashboardData({ cityId: selectedCityId });

  const refreshAll = useCallback(() => { refresh(); fetchSimulados(); }, [refresh, fetchSimulados]);

  // Dados do simulado
  const sim = simData || {};
  const summary = sim.summary || {};
  const avgBySchool = sim.avg_by_school || [];
  const avgByGrade = sim.avg_by_grade || [];
  const avgByClass = sim.avg_by_class || [];
  const avg5ano = sim.avg_5ano_by_school || [];
  const evolution = sim.evolution || [];
  const distribution = sim.distribution || [];

  // Listas de filtros vindas do backend
  const filterSchools = sim.filters?.schools || [];
  const filterGrades = sim.filters?.grades || [];

  const descriptors = useMemo(() => sim.descriptors || [], [sim.descriptors]);
  const filterClasses = useMemo(() => sim.filters?.classes || [], [sim.filters?.classes]);

  // Contagens dos cards de resumo. Quando o backend devolve `totals` (já
  // respeitando escola/ano/turma), os cards passam a acompanhar o filtro;
  // enquanto isso não chega (1º carregamento), cai no total do município
  // vindo de `useDashboardData`.
  const simTotals = sim.totals || null;

  // Filtrar turmas pelo escola/ano selecionado (client-side para UX)
  const filteredClasses = useMemo(() => {
    let cls = filterClasses;
    if (selectedSchoolId) cls = cls.filter(c => c.id_school === Number(selectedSchoolId));
    if (selectedGrade) cls = cls.filter(c => c.grade === selectedGrade);
    return cls;
  }, [filterClasses, selectedSchoolId, selectedGrade]);
  const hasActiveFilter = selectedSchoolId || selectedGrade || selectedClassId;

  // Contagens (Alunos, Escolas, Turmas) recalculadas conforme os filtros
  // ativos, para que os cards reflitam apenas o escopo filtrado (ex.: total de
  // alunos da escola selecionada, e não do município inteiro). Quando não há
  // filtro ativo, mantém os totais globais vindos de `stats`.
  const filteredTotals = useMemo(() => {
    if (!hasActiveFilter) return null;
    let cls = classes || [];
    if (selectedSchoolId) cls = cls.filter(c => c.id_school === Number(selectedSchoolId));
    if (selectedGrade) cls = cls.filter(c => c.grade === selectedGrade);
    if (selectedClassId) cls = cls.filter(c => c.id === Number(selectedClassId));
    const classIds = new Set(cls.map(c => c.id));
    const st = (students || []).filter(s => classIds.has(s.id_class));
    const activeSt = st.filter(s => ehAlunoMatriculado(s.status)).length;
    const schoolIds = new Set(cls.map(c => c.id_school));
    return {
      students: st.length,
      activeStudents: activeSt,
      schools: schoolIds.size,
      classes: cls.length,
    };
  }, [hasActiveFilter, classes, students, selectedSchoolId, selectedGrade, selectedClassId]);

  // Valores exibidos nos cards, em ordem de confiança:
  //
  // 1. `sim.totals` — o backend já contou respeitando escola/ano/turma, e
  //    conta a rede inteira;
  // 2. `filteredTotals` — recontagem no cliente enquanto o backend não
  //    responde, limitada ao que já foi carregado;
  // 3. os totais globais de `stats`, no primeiro render.
  const totalStudents =
    simTotals?.students ?? filteredTotals?.students ?? stats?.totals?.students ?? 0;
  const totalActiveStudents =
    simTotals?.active_students
    ?? filteredTotals?.activeStudents
    ?? stats?.totals?.activeStudents
    ?? 0;
  const totalSchools =
    simTotals?.schools ?? filteredTotals?.schools ?? stats?.totals?.schools ?? 0;
  const totalClasses =
    simTotals?.classes ?? filteredTotals?.classes ?? stats?.totals?.classes ?? 0;

  // Sem resultado lançado, nota e aprovação são N/D — e não 0, nem vermelho:
  // o card de aprovação pintava o "N/D" de vermelho como se a rede tivesse
  // reprovado. Com resultado, 0 é 0 (antes um 0% real também virava N/D).
  const semResultados = !summary.total_results;

  // Dados de descritores por linha (para gráfico de linha)
  const descriptorLineData = useMemo(() => {
    if (!descriptors.length) return [];
    return descriptors.slice(0, 15).map(d => ({
      code: d.code,
      rate: d.rate,
      name: d.name,
    }));
  }, [descriptors]);

  // Tooltip colors
  const axisColor = darkMode ? '#94a3b8' : '#64748b';
  const gridColor = darkMode ? '#334155' : '#e2e8f0';

  // Estilo compartilhado para selects
  const selectClass = `px-3 py-2 rounded-xl border text-sm font-medium transition-all duration-200 ${darkMode
    ? 'bg-slate-800/80 border-slate-600/50 text-slate-200 focus:border-sideb-blue focus:ring-2 focus:ring-sideb-blue/20'
    : 'bg-white border-slate-200 text-slate-700 focus:border-sideb-blue focus:ring-2 focus:ring-sideb-blue/20 shadow-sm'
  } hover:border-sideb-blue cursor-pointer`;

  return (
    <>
      {/* Botao de atualizar */}
      <button
        type="button"
        onClick={() => { refresh(); fetchSimulados(); }}
        disabled={loading || simLoading}
        className={`fixed bottom-5 right-20 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 z-50 ${darkMode
          ? 'bg-slate-800/80 border border-slate-600/15 text-sideb-blue hover:bg-slate-700/80'
          : 'bg-white/80 border border-slate-200 text-[#0974BA] hover:bg-slate-100/80 shadow-lg'
        } ${loading ? 'animate-spin' : ''}`}
        title="Atualizar dados"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </button>

      {/* Header */}
      <header className="mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-5">
          <div>
            <h1 className={`ml-3 text-3xl font-medium bg-clip-text text-transparent ${darkMode
              ? 'bg-gradient-to-r from-gray-50 to-gray-400'
              : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
            }`}>
              Dashboard de Simulados
              {(userCity || selectedCityName) && (
                <span className="ml-3 text-lg font-medium" style={{
                  background: darkMode
                    ? 'linear-gradient(135deg, #0974BA, #13983D)'
                    : 'linear-gradient(135deg, #0974BA, #F9B047)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  - {selectedCityName || userCity?.name}
                </span>
              )}
            </h1>
            <p className={`mt-2 ml-3 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
              Analise de desempenho nos simulados - Resultados por escola, turma, ano e descritores
            </p>
          </div>
          <button
            onClick={refreshAll}
            className={`px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition-all hover:-translate-y-0.5 ${darkMode
              ? 'bg-slate-700/50 border border-slate-600/50 text-slate-300 hover:bg-slate-700'
              : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 shadow-sm'
            }`}
            title="Atualizar dados"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Atualizar
          </button>
        </div>

        {/* ── Barra de Filtros ── */}
        <div className={`relative z-30 rounded-2xl p-4 border backdrop-blur-xl ${
          darkMode ? 'bg-slate-800/50 border-slate-700/30' : 'bg-white/80 border-slate-200 shadow-sm'
        }`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className={`text-xs font-semibold uppercase tracking-wider ${
              darkMode ? 'text-slate-400' : 'text-slate-500'
            }`}>
              Filtros
            </span>

            <div className={`w-px h-6 ${darkMode ? 'bg-slate-600' : 'bg-slate-300'}`} />

            {/* Cidade selecionada no sidebar */}
            {isAdmin && selectedCityName && (
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                  darkMode ? 'bg-[#0974BA]/15 text-[#4FA8E0]' : 'bg-[#0974BA]/10 text-[#0974BA]'
                }`}
                title="Filtro de cidade definido no menu lateral"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {selectedCityName}
              </span>
            )}

            {/* Escola */}
            <SearchableSelect
              value={selectedSchoolId || ''}
              onChange={(v) => setSelectedSchoolId(v ? Number(v) : null)}
              options={filterSchools.map(s => ({
                value: s.id,
                // Nome completo: o SearchableSelect trunca visualmente com CSS
                // e mostra o nome inteiro no tooltip (title) ao passar o mouse.
                label: s.school,
              }))}
              emptyOptionLabel="Todas as Escolas"
              darkMode={darkMode}
              className="min-w-[200px]"
            />

            {/* Ano/Serie */}
            <SearchableSelect
              value={selectedGrade || ''}
              onChange={(v) => setSelectedGrade(v || null)}
              options={filterGrades}
              emptyOptionLabel="Todos os Anos"
              darkMode={darkMode}
              className="min-w-[140px]"
            />

            {/* Turma */}
            <SearchableSelect
              value={selectedClassId || ''}
              onChange={(v) => setSelectedClassId(v ? Number(v) : null)}
              options={filteredClasses.map(c => ({
                value: c.id,
                label: `${c.class_name}${c.grade ? ` (${c.grade})` : ''}`,
              }))}
              emptyOptionLabel="Todas as Turmas"
              darkMode={darkMode}
              className="min-w-[160px]"
            />

            {/* Limpar filtros */}
            {hasActiveFilter && (
              <button
                type="button"
                onClick={() => {
                  setSelectedSchoolId(null);
                  setSelectedGrade(null);
                  setSelectedClassId(null);
                }}
                className={`px-3 py-2 rounded-xl text-xs font-medium transition-all duration-200 ${darkMode
                  ? 'bg-[#E83A3C]/10 text-[#E83A3C] hover:bg-[#E83A3C]/20 border border-[#E83A3C]/30'
                  : 'bg-[#E83A3C]/5 text-[#E83A3C] hover:bg-[#E83A3C]/10 border border-[#E83A3C]/20'
                }`}
              >
                Limpar Filtros
              </button>
            )}

            {/* Indicador de loading dos simulados */}
            {simLoading && (
              <div className={`flex items-center gap-2 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Atualizando...
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Conteudo */}
      {loading ? (
        <LoadingSpinnerAdvanced
          darkMode={darkMode}
          message="Carregando Dashboard..."
          subtitle={userCity ? `Buscando dados de ${userCity.name}...` : 'Buscando dados dos simulados...'}
          size="xl"
          animationType="spin-pulse"
        />
      ) : error ? (
        <ErrorDisplay error={error} onRetry={refresh} darkMode={darkMode} />
      ) : (
        <>
          {/* ── Cards de Resumo - Linha 1 ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <StatMiniCard
              title="Total de Alunos"
              value={totalStudents.toLocaleString('pt-BR')}
              subtitle={`${totalActiveStudents.toLocaleString('pt-BR')} ativos${hasActiveFilter ? ' (filtrado)' : ''}`}
              icon="🎓"
              color="pink"
              darkMode={darkMode}
            />
            <StatMiniCard
              title="Alunos Avaliados"
              value={(summary.total_students_evaluated || 0).toLocaleString('pt-BR')}
              subtitle="Participantes"
              icon="👥"
              color="teal"
              darkMode={darkMode}
            />
            <StatMiniCard
              title="Escolas"
              value={totalSchools}
              subtitle={hasActiveFilter ? 'No filtro' : 'Cadastradas'}
              icon="🏫"
              color="orange"
              darkMode={darkMode}
            />
            <StatMiniCard
              title="Turmas"
              value={totalClasses}
              subtitle={hasActiveFilter ? 'No filtro' : 'Cadastradas'}
              icon="📚"
              color="green"
              darkMode={darkMode}
            />
          </div>

          {/* ── Cards de Resumo - Linha 2 ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <StatMiniCard
              title="Simulados Aplicados"
              value={summary.total_simulados || 0}
              subtitle="Total"
              icon="📝"
              color="blue"
              darkMode={darkMode}
            />
            <StatMiniCard
              title="Nota Media Geral"
              value={semResultados ? 'N/D' : (summary.avg_score || 0).toFixed(1)}
              subtitle="Escala 0-10"
              icon="📊"
              color="purple"
              darkMode={darkMode}
            />
            <StatMiniCard
              title="Taxa de Aprovacao"
              value={semResultados ? 'N/D' : `${summary.approval_rate || 0}%`}
              subtitle={`>=60% aprovado`}
              icon="✅"
              color={semResultados ? 'purple'
                : summary.approval_rate >= 60 ? 'green' : summary.approval_rate >= 40 ? 'orange' : 'red'}
              darkMode={darkMode}
            />
          </div>

          {/* ── Linha 1: Nota por Escola + Nota por Ano ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Nota Media por Escola (com linha media estadual) */}
            <ChartCard title="Nota Media por Escola (com media estadual)" darkMode={darkMode}>
              {avgBySchool.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={avgBySchool.slice(0, 12)} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis
                      dataKey="school"
                      tick={{ fill: axisColor, fontSize: 10 }}
                      angle={-35}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tickFormatter={(v) => v.length > 18 ? v.substring(0, 18) + '...' : v}
                    />
                    <YAxis domain={[0, 10]} tick={{ fill: axisColor, fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                    <ReferenceLine y={MEDIA_ESTADUAL} stroke={COLORS.red} strokeDasharray="5 5" strokeWidth={2}
                      label={{ value: `Média Estadual (${MEDIA_ESTADUAL})`, fill: COLORS.red, fontSize: 10, position: 'top' }}
                    />
                    <Bar dataKey="avg" name="Media" radius={[6, 6, 0, 0]} barSize={30}>
                      {avgBySchool.slice(0, 12).map((entry, i) => (
                        <Cell key={i} fill={entry.avg >= MEDIA_ESTADUAL ? COLORS.teal : COLORS.red} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart message="Nenhum resultado de simulado por escola" darkMode={darkMode} />
              )}
            </ChartCard>

            {/* Nota Media por Ano/Serie */}
            <ChartCard title="Nota Media por Ano/Serie (com media estadual)" darkMode={darkMode}>
              {avgByGrade.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={avgByGrade} margin={{ top: 5, right: 20, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis dataKey="grade" tick={{ fill: axisColor, fontSize: 12 }} />
                    <YAxis domain={[0, 10]} tick={{ fill: axisColor, fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                    <ReferenceLine y={MEDIA_ESTADUAL} stroke={COLORS.red} strokeDasharray="5 5" strokeWidth={2}
                      label={{ value: `Média Estadual (${MEDIA_ESTADUAL})`, fill: COLORS.red, fontSize: 10, position: 'top' }}
                    />
                    <Bar dataKey="avg" name="Media" radius={[6, 6, 0, 0]} barSize={40}>
                      {avgByGrade.map((entry, i) => (
                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart message="Nenhum resultado de simulado por ano" darkMode={darkMode} />
              )}
            </ChartCard>
          </div>

          {/* ── Linha 2: Nota por Turma + Media 5 ano por Escola ──
              O gráfico do 5º ano (referência das avaliações SAEB/Prova Brasil)
              só aparece quando há dados desse ano; caso contrário, a "Nota
              Média por Turma" ocupa a linha inteira, evitando um gráfico vazio
              com título que confundia o usuário. */}
          <div className={`grid grid-cols-1 ${avg5ano.length > 0 ? 'lg:grid-cols-2' : ''} gap-6 mb-6`}>
            {/* Nota Media por Turma */}
            <ChartCard title="Nota Media por Turma" darkMode={darkMode}>
              {avgByClass.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={avgByClass.slice(0, 15)} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis type="number" domain={[0, 10]} tick={{ fill: axisColor, fontSize: 11 }} />
                    <YAxis
                      type="category"
                      dataKey="class_name"
                      width={120}
                      tick={{ fill: axisColor, fontSize: 10 }}
                      tickFormatter={(v) => v.length > 18 ? v.substring(0, 18) + '...' : v}
                    />
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload;
                      return (
                        <div className={`rounded-xl px-4 py-3 shadow-xl border text-sm ${
                          darkMode ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
                        }`}>
                          <p className="font-bold">{d.class_name}</p>
                          <p className="text-xs opacity-70">{d.school} - {d.grade}</p>
                          <p className="mt-1">Media: <span className="font-bold">{d.avg.toFixed(1)}</span></p>
                          <p className="text-xs">{d.count} resultados</p>
                        </div>
                      );
                    }} />
                    <ReferenceLine x={MEDIA_ESTADUAL} stroke={COLORS.red} strokeDasharray="5 5" strokeWidth={2} />
                    <Bar dataKey="avg" name="Media" radius={[0, 6, 6, 0]} barSize={16}>
                      {avgByClass.slice(0, 15).map((entry, i) => (
                        <Cell key={i} fill={entry.avg >= MEDIA_ESTADUAL ? COLORS.blue : COLORS.yellow} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart message="Nenhum resultado de simulado por turma" darkMode={darkMode} />
              )}
            </ChartCard>

            {/* Media 5 ano por Escola — só renderiza quando há dados de 5º ano */}
            {avg5ano.length > 0 && (
            <ChartCard
              title="Média do 5º Ano por Escola (referência SAEB)"
              subtitle="O 5º ano é a série de referência do IDEB dos Anos Iniciais. Só aparece quando há turmas de 5º ano com simulados aplicados."
              darkMode={darkMode}
            >
              {avg5ano.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={avg5ano.slice(0, 10)} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis
                      dataKey="school"
                      tick={{ fill: axisColor, fontSize: 10 }}
                      angle={-35}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tickFormatter={(v) => v.length > 18 ? v.substring(0, 18) + '...' : v}
                    />
                    <YAxis domain={[0, 10]} tick={{ fill: axisColor, fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                    <ReferenceLine y={MEDIA_ESTADUAL} stroke={COLORS.red} strokeDasharray="5 5" strokeWidth={2}
                      label={{ value: `Média Estadual (${MEDIA_ESTADUAL})`, fill: COLORS.red, fontSize: 10, position: 'top' }}
                    />
                    <Bar dataKey="avg" name="Media 5o Ano" radius={[6, 6, 0, 0]} barSize={30}>
                      {avg5ano.slice(0, 10).map((entry, i) => (
                        <Cell key={i} fill={entry.avg >= MEDIA_ESTADUAL ? COLORS.green : COLORS.red} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart message="Nenhum dado de 5º ano disponível" darkMode={darkMode} />
              )}
            </ChartCard>
            )}
          </div>

          {/* ── Linha 3: Descritores x Taxa de Acertos (Candlestick) ── */}
          <div className="grid grid-cols-1 gap-6 mb-6">
            <ChartCard title="Descritores x Taxa de Acertos (Min / Media / Max)" darkMode={darkMode} fullWidth>
              <DescriptorCandleChart data={descriptors} darkMode={darkMode} />
            </ChartCard>
          </div>

          {/* ── Linha 4: Grafico de Linha por Descritor + Evolucao Temporal ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Linha por Descritor */}
            <ChartCard title="Taxa de Acertos por Descritor" darkMode={darkMode}>
              {descriptorLineData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={descriptorLineData} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis
                      dataKey="code"
                      tick={{ fill: axisColor, fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={70}
                    />
                    <YAxis domain={[0, 100]} tick={{ fill: axisColor, fontSize: 11 }}
                      label={{ value: '%', angle: -90, position: 'insideLeft', style: { fill: axisColor } }}
                    />
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload;
                      return (
                        <div className={`rounded-xl px-4 py-3 shadow-xl border text-sm ${
                          darkMode ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
                        }`}>
                          <p className="font-bold text-xs">{d.code}</p>
                          <p className="text-xs opacity-70 mb-1">{d.name}</p>
                          <p>Acertos: <span className="font-bold">{d.rate}%</span></p>
                        </div>
                      );
                    }} />
                    <ReferenceLine y={60} stroke={COLORS.yellow} strokeDasharray="5 5" label={{ value: 'Meta 60%', fill: COLORS.yellow, fontSize: 10 }} />
                    <Line type="monotone" dataKey="rate" stroke={COLORS.blue} strokeWidth={2.5} dot={{ r: 4, fill: COLORS.blue }}
                      activeDot={{ r: 6, fill: COLORS.blue }} name="Taxa de Acertos"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart message="Sem dados de descritores" darkMode={darkMode} />
              )}
            </ChartCard>

            {/* Evolucao Temporal */}
            <ChartCard title="Evolucao da Media ao Longo do Tempo" darkMode={darkMode}>
              {evolution.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={evolution} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis dataKey="period" tick={{ fill: axisColor, fontSize: 11 }} />
                    <YAxis domain={[0, 10]} tick={{ fill: axisColor, fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                    <ReferenceLine y={MEDIA_ESTADUAL} stroke={COLORS.red} strokeDasharray="5 5"
                      label={{ value: `Média Estadual`, fill: COLORS.red, fontSize: 10 }}
                    />
                    <Area type="monotone" dataKey="avg" fill={COLORS.blue} fillOpacity={0.1} stroke="none" />
                    <Line type="monotone" dataKey="avg" stroke={COLORS.blue} strokeWidth={3} dot={{ r: 5, fill: COLORS.blue }}
                      activeDot={{ r: 7 }} name="Media"
                    />
                    <Line type="monotone" dataKey="max" stroke={COLORS.teal} strokeWidth={1} strokeDasharray="3 3" dot={false} name="Max" />
                    <Line type="monotone" dataKey="min" stroke={COLORS.red} strokeWidth={1} strokeDasharray="3 3" dot={false} name="Min" />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart message="Sem dados de evolucao temporal" darkMode={darkMode} />
              )}
            </ChartCard>
          </div>

          {/* ── Linha 5: Distribuição de Notas + IDEB Evolution ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Histograma de Distribuição */}
            <ChartCard title="Distribuição de Notas dos Simulados" darkMode={darkMode}>
              {distribution.some(d => d.count > 0) ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={distribution} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis dataKey="range" tick={{ fill: axisColor, fontSize: 11 }} />
                    <YAxis tick={{ fill: axisColor, fontSize: 11 }}
                      label={{ value: 'Qtd Alunos', angle: -90, position: 'insideLeft', style: { fill: axisColor, fontSize: 11 } }}
                    />
                    <Tooltip content={<CustomTooltip darkMode={darkMode} suffix=" alunos" />} />
                    <Bar dataKey="count" name="Alunos" radius={[4, 4, 0, 0]}>
                      {distribution.map((entry, i) => {
                        const val = parseInt(entry.range);
                        let color = COLORS.red;
                        if (val >= 6) color = COLORS.teal;
                        else if (val >= 4) color = COLORS.yellow;
                        return <Cell key={i} fill={color} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart message="Sem dados de distribuição" darkMode={darkMode} />
              )}
            </ChartCard>

            {/* Evolucao IDEB */}
            <ChartCard title="Evolucao do IDEB Municipal" darkMode={darkMode}>
              {idebEvolution.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={idebEvolution} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis dataKey="year" tick={{ fill: axisColor, fontSize: 11 }} />
                    <YAxis domain={[3, 7]} tick={{ fill: axisColor, fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                    <Line type="monotone" dataKey="ideb" stroke={COLORS.blue} strokeWidth={3}
                      dot={{ r: 5, fill: COLORS.blue }} name="IDEB Municipal"
                    />
                    <Line type="monotone" dataKey="meta" stroke={COLORS.yellow} strokeWidth={2}
                      strokeDasharray="5 5" dot={false} name="Meta"
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart message="Nenhum dado de IDEB disponível" darkMode={darkMode} />
              )}
            </ChartCard>
          </div>

          {/* ── Linha 6: Top Escolas IDEB ── */}
          {topSchoolsByIdeb.length > 0 && (
            <div className="grid grid-cols-1 gap-6 mb-6">
              <ChartCard title="Top 5 Escolas - IDEB" darkMode={darkMode}>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={topSchoolsByIdeb} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis type="number" domain={[0, 7]} tick={{ fill: axisColor, fontSize: 11 }} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={160}
                      tick={{ fill: axisColor, fontSize: 10 }}
                      tickFormatter={(v) => v.length > 25 ? v.substring(0, 25) + '...' : v}
                    />
                    <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                    <Bar dataKey="ideb" name="IDEB" radius={[0, 6, 6, 0]} barSize={20}>
                      {topSchoolsByIdeb.map((_, i) => (
                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          )}
        </>
      )}
    </>
  );
}

/**
 * O Dashboard tem duas leituras da mesma rede, e elas respondem a perguntas
 * diferentes: os simulados dizem como a rede foi numa prova; o diário diz como
 * ela está indo no dia a dia — frequência, notas e aulas que os professores
 * lançam. Misturar as duas numa página só obrigava a rolar por uma para
 * chegar na outra.
 *
 * A aba do diário é o Painel da Rede. Antes ele convivia com um "Desempenho
 * Escolar" que lia o mesmo diário com outra régua (6,0 em vez de 7,0, outra
 * tabela de frequência) e as duas telas davam números diferentes para o
 * mesmo aluno. Agora é uma tela só.
 *
 * A escolha fica guardada: quem usa sempre a mesma não precisa reescolher a
 * cada visita. `?aba=diario` ou `?aba=simulados` na URL abre direto numa
 * delas (é por onde entra o atalho "Painel da Rede" do menu).
 */
const CHAVE_ABA = 'sideb.dashboard.aba';
const ABAS_VALIDAS = ['diario', 'simulados'];

function Dashboard() {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [abaGuardada, setAbaGuardada] = useState(() => {
    try {
      return localStorage.getItem(CHAVE_ABA) === 'simulados' ? 'simulados' : 'diario';
    } catch {
      return 'diario';
    }
  });
  const daUrl = searchParams.get('aba');
  // "Painel da Rede" escondido desta conta (Acessos por Usuário) leva junto a
  // aba Diário: ela É o Painel da Rede, só que por outra porta.
  const semDiario = abaOculta(user, '/dashboard?aba=diario');
  const aba = semDiario ? 'simulados' : (ABAS_VALIDAS.includes(daUrl) ? daUrl : abaGuardada);

  const escolher = (valor) => {
    setAbaGuardada(valor);
    try { localStorage.setItem(CHAVE_ABA, valor); } catch { /* sessão anônima */ }
    if (daUrl) setSearchParams({ aba: valor }, { replace: true });
  };

  const ABAS = [
    { valor: 'diario', rotulo: 'Diário', apoio: 'Frequência, notas e aulas lançadas' },
    { valor: 'simulados', rotulo: 'Simulados e IDEB', apoio: 'Resultados das provas aplicadas' },
  ].filter(a => !(semDiario && a.valor === 'diario'));

  return (
    <div className="p-6">
      <div
        role="tablist"
        aria-label="Tipo de desempenho"
        className={`inline-flex flex-wrap gap-1 p-1 rounded-xl mb-6 ${
          darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-100 border border-slate-200'
        }`}
      >
        {ABAS.map(a => {
          const ativa = aba === a.valor;
          return (
            <button
              key={a.valor}
              role="tab"
              aria-selected={ativa}
              onClick={() => escolher(a.valor)}
              className={`px-4 py-2.5 rounded-lg text-left transition-all ${
                ativa
                  ? 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] text-white shadow'
                  : darkMode
                  ? 'text-slate-300 hover:bg-slate-700/60'
                  : 'text-slate-600 hover:bg-white'
              }`}
            >
              <span className="block text-sm font-medium">{a.rotulo}</span>
              <span className={`block text-[11px] ${ativa ? 'text-white/75' : darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                {a.apoio}
              </span>
            </button>
          );
        })}
      </div>

      {aba === 'diario' ? <PainelRede /> : <DashboardSimulados />}
    </div>
  );
}

export default Dashboard;
